import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from '../axiosInstance';
import { LanguageContext } from '../LanguageContext';
import useWafCaptcha from '../hooks/useWafCaptcha';

function Activation() {
  const { language } = useContext(LanguageContext);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const emailQueryParam = query.get('email') || '';
  const [email, setEmail] = useState(emailQueryParam);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const successRef = useRef(null);
  const errorRef = useRef(null);
  const { ensureCaptcha, CaptchaOverlay } = useWafCaptcha();

  useEffect(() => {
    if (submitted && successRef.current) {
      successRef.current.focus();
    }
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [submitted, error]);

  useEffect(() => {
    if (emailQueryParam) {
      setEmail(emailQueryParam);
    }
  }, [emailQueryParam]);

  const text = {
    en: {
      skip: 'Skip to main content',
      heading: 'Activate your PATH account',
      intro: 'Use this page to start first-time access to PATH. Enter the email address your case manager used for your PATH account, and we will send a verification code so you can set your password.',
      email: 'Email address',
      submit: 'Send activation code',
      successHeadline: 'If the account exists, an activation code has been sent.',
      successDetail: 'When the code arrives, continue to the next step to set your PATH password.',
      continue: 'Continue to password setup',
      editEmail: 'Use a different email address',
      error: 'Something went wrong. Please try again later.'
    },
    fr: {
      skip: 'Passer au contenu principal',
      heading: 'Activez votre compte PATH',
      intro: 'Utilisez cette page pour commencer votre premier acces a PATH. Entrez l’adresse courriel que votre gestionnaire de cas a utilisee pour votre compte PATH et nous vous enverrons un code de verification afin que vous puissiez definir votre mot de passe.',
      email: 'Adresse courriel',
      submit: 'Envoyer le code d’activation',
      successHeadline: 'Si le compte existe, un code d’activation a ete envoye.',
      successDetail: 'Lorsque vous recevrez le code, passez a l’etape suivante pour definir votre mot de passe PATH.',
      continue: 'Continuer vers la creation du mot de passe',
      editEmail: 'Utiliser une autre adresse courriel',
      error: 'Une erreur est survenue. Veuillez reessayer plus tard.'
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    try {
      await ensureCaptcha();
      await axios.post('/api/auth/forgot-password', {
        email: email.trim(),
        requestRoute: '/activate-account',
        requestFlow: 'activation'
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.message || text[language].error);
    }
  };

  if (submitted) {
    const params = new URLSearchParams();
    if (email.trim()) params.set('email', email.trim());
    params.set('flow', 'activation');
    const resetUrl = `/reset-password?${params.toString()}`;
    return (
      <>
        <CaptchaOverlay />
        <a href="#main-content" className="govuk-skip-link">{text[language].skip}</a>
        <div
          role="status"
          tabIndex={-1}
          className="govuk-panel govuk-panel--confirmation success-message"
          ref={successRef}
          id="main-content"
        >
          <p className="govuk-panel__title">{text[language].successHeadline}</p>
          <p className="govuk-body">{text[language].successDetail}</p>
        </div>
        <p className="govuk-body">
          <Link className="govuk-button" to={resetUrl}>{text[language].continue}</Link>
        </p>
        <p className="govuk-body">
          <button type="button" className="govuk-button govuk-button--secondary" onClick={() => setSubmitted(false)}>
            {text[language].editEmail}
          </button>
        </p>
      </>
    );
  }

  return (
    <>
      <CaptchaOverlay />
      <a href="#main-content" className="govuk-skip-link">{text[language].skip}</a>
      <main className="govuk-main-wrapper govuk-width-container" role="main" id="main-content" tabIndex="-1">
        <h1 className="govuk-heading-l">{text[language].heading}</h1>
        <p className="govuk-hint">{text[language].intro}</p>
        <form
          onSubmit={handleSubmit}
          aria-label={language === 'fr' ? 'Formulaire d’activation du compte PATH' : 'PATH account activation form'}
          className="govuk-form-group forgot-password-form"
          autoComplete="on"
        >
          <label className="govuk-label" htmlFor="activation-email">{text[language].email}</label>
          <input
            className="govuk-input"
            id="activation-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={event => setEmail(event.target.value)}
            aria-label={text[language].email}
          />
          <div style={{ marginTop: '1.5em' }} />
          {error && (
            <div
              className="govuk-error-message error-message"
              role="alert"
              aria-live="assertive"
              tabIndex="-1"
              ref={errorRef}
            >
              {error}
            </div>
          )}
          <button type="submit" className="govuk-button">{text[language].submit}</button>
        </form>
      </main>
    </>
  );
}

export default Activation;
